import React, { useState, useEffect } from 'react';
import { apiKeyService } from '../../services/ApiKeyService';
import { Key, Check, X, RefreshCw, Trash2, Eye, EyeOff } from 'lucide-react';
import type { UserApiKeyStatus, ApiKeyProvider } from '@photomaton/shared';

interface ProviderConfig {
  id: ApiKeyProvider;
  name: string;
  description: string;
  helpUrl: string;
  placeholder: string;
}

const PROVIDERS: ProviderConfig[] = [
  {
    id: 'gemini-imagen',
    name: 'Google Gemini',
    description: 'Google AI Studio API key for image transformations',
    helpUrl: 'https://aistudio.google.com/apikey',
    placeholder: 'AIzaSy...',
  },
  // Future providers can be added here
  // {
  //   id: 'replicate',
  //   name: 'Replicate',
  //   description: 'Replicate API key for AI models',
  //   helpUrl: 'https://replicate.com/account/api-tokens',
  //   placeholder: 'r8_...',
  // },
];

export const ApiKeyTab: React.FC = () => {
  const [statuses, setStatuses] = useState<UserApiKeyStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingProvider, setSavingProvider] = useState<string | null>(null);
  const [validatingProvider, setValidatingProvider] = useState<string | null>(null);
  const [deletingProvider, setDeletingProvider] = useState<string | null>(null);
  const [apiKeys, setApiKeys] = useState<Record<string, string>>({});
  const [showKeys, setShowKeys] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadStatuses();
  }, []);

  const loadStatuses = async () => {
    try {
      setLoading(true);
      const data = await apiKeyService.getStatuses();
      setStatuses(data);
    } catch (err) {
      setError('Failed to load API key statuses');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveKey = async (provider: ApiKeyProvider) => {
    const apiKey = apiKeys[provider];
    if (!apiKey?.trim()) {
      setError('Please enter an API key');
      return;
    }

    try {
      setSavingProvider(provider);
      setError(null);
      setSuccess(null);

      const response = await apiKeyService.saveApiKey(provider, apiKey.trim());

      if (response.data.isValid) {
        setSuccess(`API key for ${getProviderName(provider)} saved and validated successfully!`);
        setApiKeys({ ...apiKeys, [provider]: '' });
      } else {
        setError(response.data.validationError || 'API key validation failed');
      }

      // Reload statuses
      await loadStatuses();
    } catch (err) {
      setError('Failed to save API key');
      console.error(err);
    } finally {
      setSavingProvider(null);
    }
  };

  const handleValidateKey = async (provider: ApiKeyProvider) => {
    try {
      setValidatingProvider(provider);
      setError(null);
      setSuccess(null);

      const response = await apiKeyService.validateApiKey(provider);

      if (response.data.isValid) {
        setSuccess(`API key for ${getProviderName(provider)} is valid!`);
      } else {
        setError(response.data.validationError || 'API key is no longer valid');
      }

      // Reload statuses
      await loadStatuses();
    } catch (err) {
      setError('Failed to validate API key');
      console.error(err);
    } finally {
      setValidatingProvider(null);
    }
  };

  const handleDeleteKey = async (provider: ApiKeyProvider) => {
    if (!confirm(`Are you sure you want to delete your ${getProviderName(provider)} API key?`)) {
      return;
    }

    try {
      setDeletingProvider(provider);
      setError(null);
      setSuccess(null);

      await apiKeyService.deleteApiKey(provider);
      setSuccess(`API key for ${getProviderName(provider)} deleted successfully`);

      // Reload statuses
      await loadStatuses();
    } catch (err) {
      setError('Failed to delete API key');
      console.error(err);
    } finally {
      setDeletingProvider(null);
    }
  };

  const getProviderName = (providerId: string): string => {
    return PROVIDERS.find(p => p.id === providerId)?.name || providerId;
  };

  const getStatus = (provider: ApiKeyProvider): UserApiKeyStatus | undefined => {
    return statuses.find(s => s.provider === provider);
  };

  const toggleShowKey = (provider: string) => {
    setShowKeys({ ...showKeys, [provider]: !showKeys[provider] });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 text-zinc-400 animate-spin" />
        <span className="ml-2 text-zinc-400">Loading...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
          <Key className="w-5 h-5" />
          Your API Keys
        </h3>
        <p className="text-zinc-400 text-sm mb-6">
          Add your own API keys to get unlimited photo transformations. Your keys are encrypted and stored securely.
        </p>
      </div>

      {/* Status messages */}
      {error && (
        <div className="bg-red-900/50 border border-red-700 rounded-lg p-4 flex items-center gap-2">
          <X className="w-5 h-5 text-red-400 flex-shrink-0" />
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-900/50 border border-green-700 rounded-lg p-4 flex items-center gap-2">
          <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
          <p className="text-green-400 text-sm">{success}</p>
        </div>
      )}

      {/* Provider cards */}
      <div className="space-y-4">
        {PROVIDERS.map((provider) => {
          const status = getStatus(provider.id);
          const isProcessing = savingProvider === provider.id ||
                              validatingProvider === provider.id ||
                              deletingProvider === provider.id;

          return (
            <div
              key={provider.id}
              className="bg-zinc-800 rounded-lg p-6 border border-zinc-700"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h4 className="text-white font-medium text-lg">{provider.name}</h4>
                  <p className="text-zinc-400 text-sm mt-1">{provider.description}</p>
                </div>
                {status?.hasKey && (
                  <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                    status.isValid
                      ? 'bg-green-600/20 text-green-400 border border-green-600/30'
                      : 'bg-red-600/20 text-red-400 border border-red-600/30'
                  }`}>
                    {status.isValid ? 'Valid' : 'Invalid'}
                  </div>
                )}
              </div>

              {status?.hasKey ? (
                // Key exists - show management options
                <div className="space-y-4">
                  <div className="bg-zinc-900 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-zinc-400 text-sm">Current key: </span>
                        <span className="text-white font-mono">{status.keyHint}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleValidateKey(provider.id)}
                          disabled={isProcessing}
                          className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded-lg transition-colors disabled:opacity-50"
                          title="Re-validate key"
                        >
                          <RefreshCw className={`w-4 h-4 ${validatingProvider === provider.id ? 'animate-spin' : ''}`} />
                        </button>
                        <button
                          onClick={() => handleDeleteKey(provider.id)}
                          disabled={isProcessing}
                          className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded-lg transition-colors disabled:opacity-50"
                          title="Delete key"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    {status.lastValidatedAt && (
                      <p className="text-zinc-500 text-xs mt-2">
                        Last validated: {new Date(status.lastValidatedAt).toLocaleString()}
                      </p>
                    )}
                    {status.validationError && (
                      <p className="text-red-400 text-xs mt-2">
                        Error: {status.validationError}
                      </p>
                    )}
                  </div>

                  {/* Option to update the key */}
                  <div className="border-t border-zinc-700 pt-4">
                    <p className="text-zinc-400 text-sm mb-3">Update your API key:</p>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <input
                          type={showKeys[provider.id] ? 'text' : 'password'}
                          value={apiKeys[provider.id] || ''}
                          onChange={(e) => setApiKeys({ ...apiKeys, [provider.id]: e.target.value })}
                          placeholder={provider.placeholder}
                          className="w-full bg-zinc-900 text-white rounded-lg px-3 py-2 pr-10 border border-zinc-700 focus:border-zinc-500 focus:outline-none font-mono text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => toggleShowKey(provider.id)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                        >
                          {showKeys[provider.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <button
                        onClick={() => handleSaveKey(provider.id)}
                        disabled={isProcessing || !apiKeys[provider.id]?.trim()}
                        className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {savingProvider === provider.id ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <Check className="w-4 h-4" />
                        )}
                        Update
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                // No key - show input form
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <input
                        type={showKeys[provider.id] ? 'text' : 'password'}
                        value={apiKeys[provider.id] || ''}
                        onChange={(e) => setApiKeys({ ...apiKeys, [provider.id]: e.target.value })}
                        placeholder={provider.placeholder}
                        className="w-full bg-zinc-900 text-white rounded-lg px-3 py-2 pr-10 border border-zinc-700 focus:border-zinc-500 focus:outline-none font-mono text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => toggleShowKey(provider.id)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                      >
                        {showKeys[provider.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <button
                      onClick={() => handleSaveKey(provider.id)}
                      disabled={isProcessing || !apiKeys[provider.id]?.trim()}
                      className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {savingProvider === provider.id ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Key className="w-4 h-4" />
                      )}
                      Save Key
                    </button>
                  </div>
                  <p className="text-zinc-500 text-xs">
                    Get your API key from{' '}
                    <a
                      href={provider.helpUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300 underline"
                    >
                      {provider.helpUrl}
                    </a>
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Info box */}
      <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700">
        <h4 className="text-zinc-300 font-medium mb-2">How API keys work</h4>
        <ul className="text-zinc-400 text-sm space-y-1">
          <li>- Your API keys are encrypted before storage</li>
          <li>- With a valid API key, you get unlimited photo transformations</li>
          <li>- Your key is used only for your own photos</li>
          <li>- You can delete your key at any time</li>
          <li>- API usage costs are billed directly to your account by the provider</li>
        </ul>
      </div>
    </div>
  );
};
